import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { SyntheticEvent, useEffect, useState } from "react";
import useStudyEditorsStore from "../ManageStudy/useStudyEditorsStore";
import { User } from "../models";

interface AddStudyEditorsProps {
  editors: string[]; // list of editor as USIX ID's
  onSaveEditors: (editors: User[]) => void;
}

export default function AddStudyEditors({ editors, onSaveEditors }: AddStudyEditorsProps) {
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const {
    query: { data: editorsList, isLoading: isEditorsLoading },
    getInitials,
  } = useStudyEditorsStore();

  // Using the input list of USIX ID's, look up the user objects from the editorsList
  useEffect(() => {
    if (editorsList) {
      const _editors = editorsList.filter((editor) => editors.includes(editor.display_name));
      setSelectedUsers(_editors);
    }
  }, [editors, editorsList]);

  const editorOptions = (editorsList ?? []) as User[];

  if (isEditorsLoading) {
    return (
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  function handleChange(
    event: SyntheticEvent<Element, Event>,
    value: User[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<User> | undefined,
  ): void {
    if (reason === "clear") {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(value);
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Autocomplete
        multiple
        value={selectedUsers}
        options={editorOptions}
        getOptionLabel={(option) => `${option.display_name} (${option.auth_user_email})`}
        renderOption={(props, option) => {
          const { key, ...prop } = props;
          return (
            <Box component="li" {...prop} sx={{ display: "flex", alignItems: "center", gap: 1 }} key={key}>
              <Avatar sx={{ bgcolor: "#1976d2" }}>{getInitials(option.display_name)}</Avatar>
              <Box>
                <Typography variant="body1">{option.display_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.auth_user_email}
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={option.user_guid}
                size="small"
                avatar={
                  <Avatar
                    sizes="small"
                    sx={{
                      bgcolor: "#004677",
                      color: "#fff !important",
                    }}
                  >
                    {getInitials(option.display_name)}
                  </Avatar>
                }
                label={option.display_name}
                {...tagProps}
              />
            );
          })
        }
        onChange={handleChange}
        filterSelectedOptions
        renderInput={(params) => <TextField {...params} label="Search Users" />}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          onClick={() => {
            onSaveEditors(selectedUsers);
          }}
          variant="contained"
          color="primary"
          disabled={editorOptions.length === 0 ? selectedUsers.length === 0 : false}
          sx={{ backgroundColor: "#003087" }}
        >
          SAVE
        </Button>
      </Box>
    </Box>
  );
}
